// Auto-recovery for failed lazy-chunk loads. A client-side navigation loads the
// target page's JS chunk on demand; when that request fails (deploy replaced
// the build the tab was loaded with, a transient network/CDN drop), Next.js
// surfaces the raw "Application error: a client-side exception has occurred"
// screen and the user is stuck until they reload by hand. A reload always
// recovers (the fresh document references the current build), so do it for
// them — once, guarded against reload loops.

const RELOAD_STAMP_KEY = "chunk-reload-guard-at";

// One automatic reload per window; a second chunk failure inside it means the
// reload did not help (origin down, corrupted cache) — let the error surface
// instead of reload-looping. The error reporter still records every occurrence.
const RELOAD_LOOP_WINDOW_MS = 30_000;

let initialized = false;

// Matches the failure shapes browsers/bundlers produce for a dead chunk:
// webpack's ChunkLoadError / "Loading chunk N failed", the dynamic-import
// equivalents in Chromium ("Failed to fetch dynamically imported module") and
// Safari/Firefox ("Importing a module script failed"), and CSS chunks.
const CHUNK_ERROR_PATTERNS: RegExp[] = [
  /ChunkLoadError/i,
  /Loading chunk [^\s]+ failed/i,
  /Loading CSS chunk [^\s]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i
];

const isChunkLoadError = (value: unknown): boolean => {
  if (!value) return false;
  const text =
    value instanceof Error ? `${value.name}: ${value.message}` : String(value);
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
};

const reloadOnce = (): void => {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_STAMP_KEY) ?? 0);
    if (Date.now() - lastReloadAt < RELOAD_LOOP_WINDOW_MS) return;
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (privacy mode) — reload without the loop
    // guard is worse than showing the error once, so bail out.
    return;
  }
  window.location.reload();
};

export const initChunkReloadGuard = (): void => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
      reloadOnce();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      reloadOnce();
    }
  });
};
